import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './PlatformModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.clawyee.com/api';
const BUSINESS_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '';

const WhatsAppPlatformModal = ({ deployment, onClose }) => {
  const { getAccessToken } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | ready | connected | error
  const [qrUrl, setQrUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [polling, setPolling] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  
  const initialized = useRef(false);

  const initSession = useCallback(async (refresh = false) => {
    try {
      if (!refresh) setStatus('loading');
      const token = getAccessToken();
      
      const sessRes = await fetch(`${API_BASE}/integrations/whatsapp/connect-session`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ deploymentId: deployment.id, refresh })
      });
      
      if (!sessRes.ok) {
        const errData = await sessRes.json();
        throw new Error(errData.error || 'Failed to initialize session');
      }
      
      const sessData = await sessRes.json();
      setQrUrl(sessData.qrUrl);
      setExpiresAt(sessData.expiresAt);
      setStatus('ready');
      setPolling(true);
    } catch (err) {
      setErrorMessage(err.message || 'Could not reach the server.');
      setStatus('error');
    }
  }, [getAccessToken, deployment.id]);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      initSession();
    }
  }, [initSession]);

  // Polling for connection status
  useEffect(() => {
    if (!polling) return;

    const poll = async () => {
      try {
        const token = getAccessToken();
        const res = await fetch(`${API_BASE}/integrations/whatsapp/status?deploymentId=${deployment.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await res.json();

        if (data.status === 'connected') {
          setStatus('connected');
          setPolling(false);
        }
      } catch (err) {
        console.warn('Status poll failed:', err);
      }
    };

    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [polling, getAccessToken, deployment.id]);

  const waLink = qrUrl || `https://wa.me/${BUSINESS_NUMBER.replace(/\D/g, '')}`;

  return ReactDOM.createPortal(
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal-content" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-title-group">
            <span className="platform-icon">💬</span>
            <h3>WhatsApp Assistant</h3>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {status === 'loading' && (
            <div className="connection-loading">
              <div className="spinner"></div>
              <p>Preparing connection...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="connection-error">
              <div className="error-icon">⚠️</div>
              <h4>Service Not Configured</h4>
              <p>{errorMessage}</p>
              <button className="btn-secondary" onClick={() => initSession(true)} style={{ marginTop: 15 }}>
                Retry Initialization
              </button>
            </div>
          )}

          {status === 'ready' && (
            <div className="bot-details-view">
              <div className="details-header">
                <span className="status-badge pending">PENDING CONNECTION</span>
                <h4>Link your WhatsApp</h4>
                <p className="bot-description">Scan the QR or click the link below and **send the pre-filled message** to connect.</p>
              </div>

              {/* QR Code */}
              <div className="wa-qr-panel">
                <div className="wa-qr-box">
                  <QRCodeSVG
                    value={waLink}
                    size={200}
                    bgColor="#ffffff"
                    fgColor="#111111"
                    level="M"
                    includeMargin={true}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <p className="wa-qr-hint">Scan to start chatting on WhatsApp</p>
                  <button 
                    className="btn-link" 
                    onClick={() => initSession(true)}
                    style={{ fontSize: '0.75rem', opacity: 0.7 }}
                  >
                    Refresh QR Code
                  </button>
                </div>
              </div>

              <div className="details-actions">
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary-large"
                >
                  Open in WhatsApp
                </a>
                <div className="polling-indicator">
                  <div className="pulse-dot"></div>
                  <span>Waiting for your message...</span>
                </div>
                <p className="wa-consent-text">
                  By connecting WhatsApp, you agree to our <Link to="/privacy" target="_blank">Privacy Policy</Link> and <Link to="/terms" target="_blank">Terms of Service</Link>.
                </p>
              </div>
            </div>
          )}

          {status === 'connected' && (
            <div className="bot-details-view success-state">
              <div className="success-icon">✅</div>
              <h4>WhatsApp Linked!</h4>
              <p>Your WhatsApp account is now linked to **{deployment?.name}**.</p>
              
              <div className="details-meta">
                <div className="meta-item">
                  <span className="label">Status</span>
                  <span className="value success">Connected</span>
                </div>
              </div>

              <button className="btn-secondary" onClick={onClose} style={{ marginTop: 20 }}>
                Finish Setup
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-note">
            <span className="info-tag">How it works</span>
            <p>
              Customers message your business number. The webhook delivers it to this AI agent,
              which replies automatically via the Meta Cloud API.
            </p>
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default WhatsAppPlatformModal;

